import { z } from 'zod';
/**
 * Line Item Exclusion Rules — BLU-374
 *
 * Admin-defined rules that cause Shopify order line items to be skipped
 * during production batch creation. Four dropdown-driven match modes:
 *
 *   - shopify_product_tag   : matched against order_items.product_tags[]
 *   - shopify_product_type  : matched against order_items.product_type
 *   - shopify_product_id    : exact match on Shopify product GID / numeric ID
 *   - shopify_variant_id    : exact match on Shopify variant GID / numeric ID
 *
 * One rule per selection — picking 3 tags creates 3 rows.
 */
// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------
export const MatchFieldSchema = z.enum([
    'shopify_product_tag',
    'shopify_product_type',
    'shopify_product_id',
    'shopify_variant_id',
]);
export const InheritModeSchema = z.enum(['extend', 'replace']);
// ---------------------------------------------------------------------------
// Full rule row (mirrors DB schema)
// ---------------------------------------------------------------------------
export const ExclusionRuleRowSchema = z.object({
    id: z.string().uuid(),
    organization_id: z.string().uuid(),
    location_id: z.string().uuid().nullable(),
    match_field: MatchFieldSchema,
    value: z.string().min(1).max(500),
    display_label: z.string().max(500).nullable().optional(),
    is_active: z.boolean(),
    priority: z.number().int().min(0).max(9999),
    created_by: z.string().uuid().nullable().optional(),
    created_at: z.string(),
    updated_at: z.string(),
});
// ---------------------------------------------------------------------------
// Create / update inputs (admin UI)
// ---------------------------------------------------------------------------
export const CreateExclusionRuleInputSchema = z.object({
    match_field: MatchFieldSchema,
    value: z.string().min(1).max(500),
    display_label: z.string().max(500).optional(),
    location_id: z.string().uuid().nullable().optional(),
    priority: z.number().int().min(0).max(9999).default(100),
    is_active: z.boolean().default(true),
});
/**
 * Bulk create — multi-select picker in UI creates one row per selected value.
 * Each item in `selections` becomes a separate rule.
 */
export const CreateExclusionRuleBatchInputSchema = z.object({
    match_field: MatchFieldSchema,
    location_id: z.string().uuid().nullable().optional(),
    priority: z.number().int().min(0).max(9999).default(100),
    is_active: z.boolean().default(true),
    selections: z
        .array(z.object({
        value: z.string().min(1).max(500),
        display_label: z.string().max(500).optional(),
    }))
        .min(1)
        .max(100),
});
export const UpdateExclusionRuleInputSchema = z.object({
    id: z.string().uuid(),
    value: z.string().min(1).max(500).optional(),
    display_label: z.string().max(500).nullable().optional(),
    priority: z.number().int().min(0).max(9999).optional(),
    is_active: z.boolean().optional(),
});
export const DeleteExclusionRuleInputSchema = z.object({
    id: z.string().uuid(),
});
// ---------------------------------------------------------------------------
// Location override settings
// ---------------------------------------------------------------------------
export const LocationSettingsRowSchema = z.object({
    location_id: z.string().uuid(),
    organization_id: z.string().uuid(),
    inherit_mode: InheritModeSchema,
    updated_at: z.string(),
});
export const UpdateLocationSettingsInputSchema = z.object({
    location_id: z.string().uuid(),
    inherit_mode: InheritModeSchema,
});
// ---------------------------------------------------------------------------
// Matcher input shape — a normalized line item
// ---------------------------------------------------------------------------
// Consumes the minimum fields needed by the 4 match_fields. Callers build this
// from either order_items or prod_order_items rows (both are sources of truth
// during different parts of the batch-creation flow).
export const ExclusionItemShapeSchema = z.object({
    sku: z.string().nullable().optional(),
    product_type: z.string().nullable().optional(),
    product_tags: z.array(z.string()).nullable().optional(),
    shopify_product_id: z.union([z.string(), z.number()]).nullable().optional(),
    shopify_variant_id: z.union([z.string(), z.number()]).nullable().optional(),
});
// ---------------------------------------------------------------------------
// Preview input
// ---------------------------------------------------------------------------
export const PreviewExclusionRuleInputSchema = z.object({
    match_field: MatchFieldSchema,
    value: z.string().min(1).max(500),
    location_id: z.string().uuid().nullable().optional(),
});
// ---------------------------------------------------------------------------
// Shopify search inputs
// ---------------------------------------------------------------------------
export const ShopifySearchInputSchema = z.object({
    query: z.string().min(0).max(100).default(''),
    limit: z.number().int().min(1).max(50).default(20),
});
